/**
 * Cyberpunk HUD Component
 * 
 * Interface futurista com elementos de HUD animados
 */

import React from 'react'
import { motion } from 'framer-motion'

// Canto decorativo do HUD
export const HUDCorner: React.FC<{ position: 'tl' | 'tr' | 'bl' | 'br' }> = ({ position }) => {
    const rotations = {
        tl: 'rotate-0',
        tr: 'rotate-90',
        bl: '-rotate-90',
        br: 'rotate-180'
    }

    const positions = {
        tl: 'top-0 left-0',
        tr: 'top-0 right-0',
        bl: 'bottom-0 left-0',
        br: 'bottom-0 right-0'
    }

    return (
        <div className={`absolute ${positions[position]} w-12 h-12 ${rotations[position]}`}>
            <svg viewBox="0 0 48 48" className="w-full h-full">
                <path
                    d="M2 46 L2 12 L12 2 L46 2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-cyan-400/60"
                />
                <circle cx="12" cy="12" r="2" fill="currentColor" className="text-cyan-400" />
            </svg>
        </div>
    )
}

// Linha de scanning animada
export const ScanLine: React.FC = () => (
    <motion.div
        className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-30"
        initial={{ top: '0%' }}
        animate={{ top: ['0%', '100%', '0%'] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
    />
)

// Indicador de status
export const StatusIndicator: React.FC<{ status: 'online' | 'offline' | 'dev'; label: string }> = ({ status, label }) => {
    const colors = {
        online: 'bg-green-400',
        offline: 'bg-red-400',
        dev: 'bg-yellow-400'
    }

    return (
        <div className="flex items-center gap-2">
            <motion.div
                className={`w-2 h-2 rounded-full ${colors[status]}`}
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-xs text-white/60 font-mono uppercase tracking-wider">{label}</span>
        </div>
    )
}

// Barra de progresso futurista
export const CyberProgressBar: React.FC<{ value: number; color?: string }> = ({ value, color = '#00f5ff' }) => (
    <div className="relative h-1 bg-white/10 rounded overflow-hidden">
        <motion.div
            className="h-full rounded"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${value}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
        />
        <motion.div
            className="absolute top-0 right-0 bottom-0 w-8"
            style={{ background: `linear-gradient(to right, transparent, ${color})` }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
        />
    </div>
)

// Container com borda de HUD
export const HUDContainer: React.FC<{ children: React.ReactNode; className?: string; title?: string }> = ({
    children,
    className = '',
    title
}) => (
    <motion.div
        className={`relative ${className}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
    >
        {/* Border glow effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-cyan-500/20 rounded-lg blur-sm" />

        {/* Main container */}
        <div className="relative bg-black/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg overflow-hidden">
            {/* Corners */}
            <HUDCorner position="tl" />
            <HUDCorner position="tr" />
            <HUDCorner position="bl" />
            <HUDCorner position="br" />

            {/* Scan line */}
            <ScanLine />

            {/* Title bar */}
            {title && (
                <div className="px-6 py-3 border-b border-cyan-500/20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <motion.div
                            className="w-2 h-2 bg-cyan-400 rounded-full"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                        <span className="text-cyan-400 font-mono text-sm uppercase tracking-widest">{title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-0.5 bg-cyan-400/30" />
                        <div className="w-4 h-0.5 bg-cyan-400/50" />
                        <div className="w-2 h-0.5 bg-cyan-400" />
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-6">
                {children}
            </div>
        </div>
    </motion.div>
)

// Botão cyberpunk
export const CyberButton: React.FC<{
    children: React.ReactNode
    onClick?: () => void
    variant?: 'primary' | 'secondary' | 'danger'
    disabled?: boolean
    className?: string
}> = ({ children, onClick, variant = 'primary', disabled = false, className = '' }) => {
    const variants = {
        primary: 'from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white',
        secondary: 'from-transparent to-transparent border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10',
        danger: 'from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500 text-white'
    }

    return (
        <motion.button
            className={`
        relative px-6 py-3 bg-gradient-to-r ${variants[variant]}
        font-mono uppercase tracking-wider text-sm
        rounded-lg overflow-hidden
        transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
            onClick={onClick}
            disabled={disabled}
            whileHover={{ scale: disabled ? 1 : 1.02 }}
            whileTap={{ scale: disabled ? 1 : 0.98 }}
        >
            {/* Glitch effect on hover */}
            <motion.span
                className="absolute inset-0 bg-white/20"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.5 }}
            />

            {/* Corners */}
            <span className="absolute top-0 left-0 w-2 h-2 border-l border-t border-current opacity-50" />
            <span className="absolute top-0 right-0 w-2 h-2 border-r border-t border-current opacity-50" />
            <span className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-current opacity-50" />
            <span className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-current opacity-50" />

            <span className="relative">{children}</span>
        </motion.button>
    )
}

// Badge de status
export const CyberBadge: React.FC<{ variant: 'available' | 'development'; children: React.ReactNode }> = ({ variant, children }) => {
    const styles = {
        available: 'bg-green-500/20 text-green-400 border-green-500/50',
        development: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
    }

    return (
        <motion.span
            className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-mono uppercase tracking-wider rounded-full border ${styles[variant]}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
        >
            <motion.span
                className={`w-1.5 h-1.5 rounded-full ${variant === 'available' ? 'bg-green-400' : 'bg-yellow-400'}`}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
            />
            {children}
        </motion.span>
    )
}

// Decorative data stream
export const DataStream: React.FC = () => (
    <div className="absolute right-4 top-1/4 bottom-1/4 w-px overflow-hidden opacity-30">
        <motion.div
            className="w-full h-20 bg-gradient-to-b from-transparent via-cyan-400 to-transparent"
            animate={{ y: ['-100%', '400%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
    </div>
)

// Stats display
export const CyberStats: React.FC<{ stats: { label: string; value: string }[] }> = ({ stats }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
            <motion.div
                key={stat.label}
                className="text-center p-3 border border-cyan-500/20 rounded-lg bg-cyan-500/5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
            >
                <div className="text-2xl font-bold text-cyan-400 font-mono">{stat.value}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{stat.label}</div>
            </motion.div>
        ))}
    </div>
)

export default {
    HUDCorner,
    ScanLine,
    StatusIndicator,
    CyberProgressBar,
    HUDContainer,
    CyberButton,
    CyberBadge,
    DataStream,
    CyberStats
}
